import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/Toast';

export default function SubmissionConfigs() {
  const { user } = useAuth();
  const toast = useToast();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingConfig, setEditingConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/submission-configs');
      setConfigs(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load configs');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (config) => {
    setEditingConfig({
      ...config,
      // Parse the config JSONB if it's a string
      config: typeof config.config === 'string' ? JSON.parse(config.config) : config.config
    });
  };

  const handleCancel = () => {
    setEditingConfig(null);
  };

  const handleConfigChange = (field, value) => {
    setEditingConfig(prev => ({
      ...prev,
      config: {
        ...prev.config,
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    if (!isAdmin) {
      toast.warning('Only admins can edit configurations');
      return;
    }

    try {
      setSaving(true);
      await api.put(
        `/submission-configs/${editingConfig.submission_type}`,
        {
          display_name: editingConfig.display_name,
          config: editingConfig.config
        }
      );

      await loadConfigs();
      setEditingConfig(null);
      toast.success('Configuration updated successfully!');
    } catch (err) {
      toast.error('Failed to save configuration: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading configurations...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Submission Configurations</h1>
              <p className="mt-2 text-sm sm:text-base text-gray-600">
                Configure Twilio submission defaults and form settings for each verification type
              </p>
            </div>
            <Link
              to="/dashboard"
              className="btn btn-secondary whitespace-nowrap"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
            <p className="font-medium">Error</p>
            <p className="text-sm">{error}</p>
          </div>
        )}

        {!isAdmin && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg p-4">
            <p className="font-medium">View Only</p>
            <p className="text-sm">You can view configurations but only admins can make changes.</p>
          </div>
        )}

        {/* Editing Mode */}
        {editingConfig ? (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                Edit {editingConfig.display_name} Configuration
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={handleCancel}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !isAdmin}
                  className="btn btn-primary"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>

            {/* Display Name */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Display Name
              </label>
              <input
                type="text"
                value={editingConfig.display_name}
                onChange={(e) => setEditingConfig(prev => ({ ...prev, display_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                disabled={!isAdmin}
              />
            </div>

            {/* Twilio Submission Fields */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b">
                Twilio Submission Settings
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Use Case Category
                  </label>
                  <select
                    value={editingConfig.config.use_case_category || ''}
                    onChange={(e) => handleConfigChange('use_case_category', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    disabled={!isAdmin}
                  >
                    <option value="CUSTOMER_CARE">Customer Care</option>
                    <option value="CHARITY_NONPROFIT">Charity / Non-Profit</option>
                    <option value="MARKETING">Marketing</option>
                    <option value="POLLING_VOTING">Polling / Voting</option>
                    <option value="PUBLIC_SERVICE_ANNOUNCEMENT">Public Service Announcement</option>
                    <option value="HIGHER_EDUCATION">Higher Education</option>
                    <option value="DELIVERY_NOTIFICATION">Delivery Notification</option>
                    <option value="ACCOUNT_NOTIFICATION">Account Notification</option>
                    <option value="2FA">Two-Factor Authentication</option>
                    <option value="FRAUD_ALERT">Fraud Alert</option>
                    <option value="SECURITY_ALERT">Security Alert</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Opt-In Type
                  </label>
                  <select
                    value={editingConfig.config.opt_in_type || ''}
                    onChange={(e) => handleConfigChange('opt_in_type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    disabled={!isAdmin}
                  >
                    <option value="VIA_TEXT">Via Text</option>
                    <option value="WEB_FORM">Web Form</option>
                    <option value="PAPER_FORM">Paper Form</option>
                    <option value="MOBILE_QR_CODE">Mobile QR Code</option>
                    <option value="VERBAL">Verbal</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message Volume
                  </label>
                  <select
                    value={editingConfig.config.message_volume || ''}
                    onChange={(e) => handleConfigChange('message_volume', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    disabled={!isAdmin}
                  >
                    <option value="1,000">~1,000 messages/month</option>
                    <option value="10,000">~10,000 messages/month</option>
                    <option value="100,000">~100,000 messages/month</option>
                    <option value="250,000">~250,000 messages/month</option>
                    <option value="500,000">~500,000 messages/month</option>
                    <option value="750,000">~750,000 messages/month</option>
                    <option value="1,000,000">~1,000,000+ messages/month</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notifications Email (optional)
                  </label>
                  <input
                    type="email"
                    value={editingConfig.config.notifications_email || ''}
                    onChange={(e) => handleConfigChange('notifications_email', e.target.value || null)}
                    placeholder="Leave empty to use contact email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    disabled={!isAdmin}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    If empty, the submission's contact email will be used
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Use Case Summary
                </label>
                <textarea
                  value={editingConfig.config.use_case_summary || ''}
                  onChange={(e) => handleConfigChange('use_case_summary', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  disabled={!isAdmin}
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sample Message Content
                </label>
                <textarea
                  value={editingConfig.config.message_content || ''}
                  onChange={(e) => handleConfigChange('message_content', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  disabled={!isAdmin}
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Opt-In Workflow Description
                </label>
                <textarea
                  value={editingConfig.config.opt_in_workflow_description || ''}
                  onChange={(e) => handleConfigChange('opt_in_workflow_description', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  disabled={!isAdmin}
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Information
                </label>
                <textarea
                  value={editingConfig.config.additional_information || ''}
                  onChange={(e) => handleConfigChange('additional_information', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  disabled={!isAdmin}
                />
              </div>

              {/* Twilio compliance fields (required by Twilio TFV as of Sept 2026) */}
              <div className="mt-6 pt-4 border-t">
                <h4 className="text-sm font-semibold text-gray-900 mb-1">
                  Compliance Links & Opt-In Language
                </h4>
                <p className="text-xs text-gray-500 mb-4">
                  Required by Twilio for Toll-Free Verification. The linked pages must
                  disclose message frequency, msg &amp; data rates, and STOP/HELP instructions.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Privacy Policy URL
                    </label>
                    <input
                      type="url"
                      value={editingConfig.config.privacy_policy_url || ''}
                      onChange={(e) => handleConfigChange('privacy_policy_url', e.target.value)}
                      placeholder="https://get.tithe.ly/legal/privacy-policy"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      disabled={!isAdmin}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Terms of Service URL
                    </label>
                    <input
                      type="url"
                      value={editingConfig.config.terms_and_conditions_url || ''}
                      onChange={(e) => handleConfigChange('terms_and_conditions_url', e.target.value)}
                      placeholder="https://get.tithe.ly/legal/terms-of-service"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      disabled={!isAdmin}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Help Message Sample
                  </label>
                  <textarea
                    value={editingConfig.config.help_message_sample || ''}
                    onChange={(e) => handleConfigChange('help_message_sample', e.target.value)}
                    rows={2}
                    placeholder="Reply HELP for help or STOP to opt out. Msg frequency varies. Msg & data rates may apply."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    disabled={!isAdmin}
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Opt-In Confirmation Message
                  </label>
                  <textarea
                    value={editingConfig.config.opt_in_confirmation_message || ''}
                    onChange={(e) => handleConfigChange('opt_in_confirmation_message', e.target.value)}
                    rows={2}
                    placeholder="You are now subscribed. Msg frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    disabled={!isAdmin}
                  />
                </div>
              </div>
            </div>

            {/* Intake Form Settings */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b">
                Intake Form Settings
              </h3>

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Form Title
                  </label>
                  <input
                    type="text"
                    value={editingConfig.config.intake_form_title || ''}
                    onChange={(e) => handleConfigChange('intake_form_title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    disabled={!isAdmin}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Form Subtitle
                  </label>
                  <input
                    type="text"
                    value={editingConfig.config.intake_form_subtitle || ''}
                    onChange={(e) => handleConfigChange('intake_form_subtitle', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    disabled={!isAdmin}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Field Label
                    </label>
                    <input
                      type="text"
                      value={editingConfig.config.phone_field_label || ''}
                      onChange={(e) => handleConfigChange('phone_field_label', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      disabled={!isAdmin}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Field Description
                    </label>
                    <input
                      type="text"
                      value={editingConfig.config.phone_field_description || ''}
                      onChange={(e) => handleConfigChange('phone_field_description', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      disabled={!isAdmin}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Help Tooltip
                  </label>
                  <textarea
                    value={editingConfig.config.phone_help_tooltip || ''}
                    onChange={(e) => handleConfigChange('phone_help_tooltip', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    disabled={!isAdmin}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Shown in the &ldquo;Where can I find this?&rdquo; tooltip on the intake form, only when the phone number isn&rsquo;t pre-filled.
                  </p>
                </div>
              </div>
            </div>

            {/* Opt-In Image Settings */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b">
                Opt-In Image Settings
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image Main Text
                  </label>
                  <input
                    type="text"
                    value={editingConfig.config.opt_in_image_text || ''}
                    onChange={(e) => handleConfigChange('opt_in_image_text', e.target.value)}
                    placeholder="e.g., 'Text GIVE to'"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    disabled={!isAdmin}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image Subtext
                  </label>
                  <input
                    type="text"
                    value={editingConfig.config.opt_in_image_subtext || ''}
                    onChange={(e) => handleConfigChange('opt_in_image_subtext', e.target.value)}
                    placeholder="e.g., 'to donate to'"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    disabled={!isAdmin}
                  />
                </div>
              </div>
            </div>

            {/* Last Updated Info */}
            <div className="text-sm text-gray-500 border-t pt-4">
              <p>Last updated: {editingConfig.updated_at ? new Date(editingConfig.updated_at).toLocaleString() : 'Never'}</p>
              {editingConfig.updated_by && <p>Updated by: {editingConfig.updated_by}</p>}
            </div>
          </div>
        ) : (
          /* List View */
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {configs.map((config) => (
              <div key={config.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {config.display_name}
                  </h2>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    config.submission_type === 'text_giving'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {config.submission_type}
                  </span>
                </div>

                <div className="space-y-3 text-sm text-gray-600 mb-4">
                  <div>
                    <span className="font-medium">Use Case Category:</span>{' '}
                    {config.config?.use_case_category || 'Not set'}
                  </div>
                  <div>
                    <span className="font-medium">Opt-In Type:</span>{' '}
                    {config.config?.opt_in_type || 'Not set'}
                  </div>
                  <div>
                    <span className="font-medium">Message Volume:</span>{' '}
                    {config.config?.message_volume || 'Not set'}
                  </div>
                  <div>
                    <span className="font-medium">Form Title:</span>{' '}
                    {config.config?.intake_form_title || 'Not set'}
                  </div>
                </div>

                <div className="text-xs text-gray-500 mb-4">
                  Last updated: {config.updated_at ? new Date(config.updated_at).toLocaleString() : 'Never'}
                </div>

                <button
                  onClick={() => handleEdit(config)}
                  className="btn btn-primary w-full"
                >
                  {isAdmin ? 'Edit Configuration' : 'View Details'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
